/**
 * The customer portal's Quotes section — the list (item 7b).
 *
 * READ-ONLY BY CONSTRUCTION. This is a separate page from `QuotesListPage`
 * rather than that page with the buttons hidden: that one carries New Quote,
 * Batch Quotes, the rights hook and row links into `/quotes/:id`, which is a
 * broker-only route. A page with no create/edit/share/delete affordance
 * anywhere in it cannot grow one by accident, and the backend agrees —
 * `QuoteRequestViewSet` refuses update/destroy/share-via/unshare to a customer
 * (`backend/tests/test_customer_portal_quotes.py`). A hidden button is not a
 * permission, and this page does not rely on being one.
 *
 * It reads the SAME endpoint the broker list reads,
 * `GET /api/v1/quotes/requests/` — the server's customer branch is what scopes
 * it to this person. That branch matches on BOTH `customer` (the login) and
 * `customer_profile` (the contact record); before item 7 it matched only the
 * login, so a broker-raised quote for a contact who had no login yet was
 * invisible even after they were given one. That is the common case, so without
 * the backend half this page would have rendered empty for most customers.
 *
 * Three states, never two: loading, failed, and empty are different facts. A
 * failed fetch rendering as "You have no quotes yet" is the platform's
 * repeat offender (`feedback_failed_request_is_not_empty`).
 *
 * C7 (2026-09-18) added the search bar and the four filters below (Insurance
 * Types, Insurance Companies, Status, a date range) -- STILL READ-ONLY.
 *
 * The Wave 3 sweep (Q-01/U-02/U-04) then put those filters in the URL, gave
 * the table fixed widths and a sticky header, and added sort controls to the
 * two columns the server can actually order by. It added NO affordance: the
 * rows still do not navigate on click, and nothing links into `/quotes/:id`.
 *
 * 2026-09-20: the parked "Request a Quote" journey now exists, so this page
 * gains ONE control -- a link to `/my-quotes/new` -- and nothing else. CREATE
 * ONLY: there is still no edit, delete, share or cancel affordance anywhere
 * here, and still no link into `/quotes/:id` (a broker-only route). The
 * journey is a separate page for exactly the reason this page is separate
 * from QuotesListPage: a screen with no write affordance in it cannot grow one
 * by accident. The server agrees independently -- `QuoteRequestViewSet` grants
 * a customer `create` and refuses update/destroy/submit/share/unshare
 * (`backend/tests/test_customer_portal_quotes.py`,
 * `backend/tests/test_customer_self_serve_quote.py`).
 */

import { ClipboardDocumentListIcon, PlusIcon } from '@heroicons/react/24/outline';
import { useEffect, useMemo, useRef, useState } from 'react';
import { Link } from 'react-router-dom';

import {
  Badge,
  Card,
  CompactFilterBar,
  DateRangePicker,
  EmptyState,
  Pagination,
  Select,
  SearchableSelect,
  Table,
  getStatusBadgeVariant,
} from '@/components/common';
import type { Column, SortDirection } from '@/components/common/Table';
import { PageHeader } from '@/components/layout';
import { useDebouncedSearch } from '@/hooks/useDebouncedSearch';
import { useFilterParams, type FilterDateRange } from '@/hooks/useFilterParams';
import { useQuoteFilterOptions } from '@/hooks';
import { formatRelativeTime, titleCase } from '@/utils';
import type { QuoteStatus } from '@/types';

import { SharedStatusBadge, type QuoteRow } from './quotesPending';
import { useQuoteList, type QuoteListFilters } from './quoteListQuery';

const ITEMS_PER_PAGE = 20;

// C7: a simplified, customer-facing 4-bucket view of the real 11-value
// QuoteRequest.status ladder (backend/apps/quotes/lifecycle.py). Each option
// sends one REAL raw status value the server already understands --
// "Requested" sends the legacy `submitted` (whose own model label IS
// "Submitted": the step right after Draft, which is what "you asked for a
// quote" means to a customer) and "Pending" sends only the raw `pending`
// value, not every in-progress KYC rung (quote_shared/kyc_pending_approval/
// kyc_approved) that also reads as "still in progress" to a customer. This is
// a deliberate simplification of the ladder for a read-only portal filter --
// matching the four buckets asked for -- not an attempt to reproduce the
// whole lifecycle.
const STATUS_FILTER_OPTIONS: { value: string; label: string }[] = [
  { value: '', label: 'All Statuses' },
  { value: 'draft', label: 'Draft' },
  { value: 'submitted', label: 'Requested' },
  { value: 'pending', label: 'Pending' },
  { value: 'completed', label: 'Completed' },
];

/**
 * Q-01 -- the filter state this page mirrors into the query string.
 *
 * Flat strings plus one `{from,to}` range, because that is what
 * `useFilterParams` can decode from a URL; `queryFilters` below turns them
 * back into the shape the API speaks (the range is two separate params, and an
 * empty string means "send nothing" rather than "send blank").
 *
 * DELIBERATELY NOT HERE: the page number. Every filter change resets it to 1,
 * so a restored number would be clobbered before the first paint and the URL
 * would then lie about what is on screen.
 */
type MyQuotePanelFilters = {
  search: string;
  insurance_type: string;
  insurer: string;
  status: string;
  /** Stored as `f_created_from` / `f_created_to`, sent as `created_from`/`created_to`. */
  created: FilterDateRange;
  /** U-04: the server ordering field, '' when the list is in its default order. */
  sort: string;
  sort_dir: string;
};

const EMPTY_FILTERS: MyQuotePanelFilters = {
  search: '',
  insurance_type: '',
  insurer: '',
  status: '',
  created: {},
  sort: '',
  sort_dir: '',
};

/**
 * Q-01 note 3: the `f_` namespace.
 *
 * Nothing deep-links into `/my-quotes` with an unprefixed scope param today,
 * so there is no seed to read and (STEP 2 of the adoption recipe) nothing for
 * a `consumeSeedParams` helper to consume. The prefix is kept anyway, matching
 * every other converted list, so a future deep link cannot collide with the
 * page's own filters -- the collision is the expensive half to retrofit.
 */
const FILTER_PREFIX = 'f_';

/**
 * U-04: the fields this page offers as sort controls.
 *
 * `QuoteRequestViewSet.ordering_fields` (backend/apps/quotes/views.py:498) is
 * exactly `["created_at", "submitted_at", "request_number"]` -- the same
 * viewset the broker list reads, because this page reads the same endpoint.
 * `submitted_at` is not a column here. Insurance and Status are NOT orderable
 * server-side: DRF's OrderingFilter drops an unknown term silently and returns
 * the default order, so a header offered for either would be a control that
 * looks like it works and changes nothing.
 */
const SORTABLE_FIELDS = new Set(['request_number', 'created_at']);

export function MyQuotesPage() {
  const [page, setPage] = useState(1);

  // Q-01: the panel's own filters, mirrored into `?f_*` so they survive a
  // refresh, a bookmark and a shared link.
  const { filters, setFilter, setFilters, clearAll } = useFilterParams<MyQuotePanelFilters>({
    defaults: EMPTY_FILTERS,
    prefix: FILTER_PREFIX,
  });

  // BROKER-9-style search-as-you-type (PoliciesListPage uses the same
  // pattern): `searchTerm` drives the box synchronously, `debouncedTerm`
  // feeds the query 300ms after typing stops. Seeded from the restored
  // `?f_search=` so a refresh brings the box back filled in, not just the
  // results.
  const { term: searchTerm, debouncedTerm, setTerm: setSearchTerm } = useDebouncedSearch(filters.search);

  // Keyed off a ref of the LAST TERM PUSHED, not off `filters.search`: seeded
  // with the restored value it makes the mount pass a no-op, and because Clear
  // resets `filters.search` while `debouncedTerm` still holds the old text for
  // another 300ms, comparing against `filters.search` would immediately
  // re-apply the search the user just cleared.
  const pushedTermRef = useRef(filters.search);
  useEffect(() => {
    if (pushedTermRef.current === debouncedTerm) return;
    pushedTermRef.current = debouncedTerm;
    setFilter('search', debouncedTerm);
    setPage(1);
  }, [debouncedTerm, setFilter]);

  // U-04: only a field the server can order by, so a stale/hand-edited
  // `?f_sort=` cannot leave a header drawn as sorted over a list that is not.
  const sortKey = SORTABLE_FIELDS.has(filters.sort) ? filters.sort : '';
  const sortDirection: SortDirection = filters.sort_dir === 'desc' ? 'desc' : 'asc';
  const ordering = sortKey ? `${sortDirection === 'desc' ? '-' : ''}${sortKey}` : undefined;

  // The wire shape. Memoized because it is the react-query key.
  const queryFilters = useMemo<QuoteListFilters>(
    () => ({
      search: filters.search || undefined,
      insurance_type: filters.insurance_type || undefined,
      insurer: filters.insurer || undefined,
      status: (filters.status || undefined) as QuoteStatus | undefined,
      created_from: filters.created.from || undefined,
      created_to: filters.created.to || undefined,
      ordering,
    }),
    [filters, ordering]
  );

  const { data, isLoading, isError, refetch } = useQuoteList(page, queryFilters);
  // C7: options scoped to the caller's OWN quote requests -- see the hook's
  // own comment for why this replaces the platform-wide types/insurers
  // lookups every OTHER page in this app uses.
  const { data: filterOptions } = useQuoteFilterOptions();

  const quotes = useMemo(() => (data?.results ?? []) as QuoteRow[], [data]);
  const totalItems = data?.count ?? 0;
  const totalPages = Math.ceil(totalItems / ITEMS_PER_PAGE);

  const insuranceTypeOptions = [
    { value: '', label: 'All Insurance Types' },
    ...(filterOptions?.insurance_types ?? []).map((option) => ({ value: option.id, label: option.name })),
  ];
  const insurerOptions = [
    { value: '', label: 'All Insurance Companies' },
    ...(filterOptions?.insurers ?? []).map((option) => ({ value: option.id, label: option.name })),
  ];

  const handleInsuranceTypeChange = (value: string) => {
    setFilter('insurance_type', value);
    setPage(1);
  };
  const handleInsurerChange = (value: string) => {
    setFilter('insurer', value);
    setPage(1);
  };
  const handleStatusChange = (value: string) => {
    setFilter('status', value);
    setPage(1);
  };
  const handleDateRangeChange = (from: string, to: string) => {
    // Both bounds in ONE update, so they land in the same render/request
    // instead of briefly disagreeing.
    const next: FilterDateRange = {};
    if (from) next.from = from;
    if (to) next.to = to;
    setFilter('created', next);
    setPage(1);
  };

  // U-04: server-side sort. `Table` never reorders the rows it is given.
  const handleSortChange = (key: string, direction: SortDirection) => {
    setFilters({ sort: key, sort_dir: direction });
    setPage(1);
  };

  const hasWindow = Boolean(filters.created.from || filters.created.to);
  const activeFilterCount = [
    filters.insurance_type,
    filters.insurer,
    filters.status,
    // The window is ONE filter the customer applied, however many bounds it
    // puts on the wire. Counting both made a picked range badge as 2.
    hasWindow ? 'window' : '',
    // The sort is a view preference, not a filter, so it is deliberately not
    // counted -- otherwise "Clear (1)" would be offered for something no
    // filter set.
  ].filter(Boolean).length;
  // The search box is not part of activeFilterCount (CompactFilterBar's own
  // convention -- search is always visible, so it does not need a badge) but
  // it still makes an empty result a FILTERED empty result, not a bare one.
  const hasActiveFilters = activeFilterCount > 0 || Boolean(filters.search);

  const clearFilters = () => {
    setSearchTerm('');
    // The one action that empties the `?f_*` namespace, the sort included.
    clearAll();
    setPage(1);
  };

  /**
   * U-02: the columns, with a width each.
   *
   * SIZE = CONTENT + CELL PADDING. `Table` pads every `<td>` with `px-6`
   * (48px) and, under `layout="fixed"`, sets `truncate` -- `overflow: hidden`
   * on a border-box cell -- so a declared width has to carry that padding or
   * the content is clipped with no scrollbar and no ellipsis. Two columns
   * override it to `px-3` (24px) through `className`; `cn` is tailwind-merge,
   * so a `px-*` there beats the `px-6`:
   *
   *   request_number  16rem  px-6 -> 208px (the quote link over the broker)
   *   insurance       14rem  px-6 -> 176px (type over business type)
   *   status          13rem  px-3 -> 184px (status badge over the shared badge)
   *   created_at      10rem  px-3 -> 136px ("about 2 months ago")
   *                  -------
   *                   53rem = 848px, comfortably inside the ~1424px a desktop
   *   can show without a horizontal page scrollbar; the fixed layout spreads
   *   the slack proportionally rather than re-deriving the columns from
   *   whichever rows happen to be on screen, which is the jump U-02 is about.
   *
   * The quote-number cell holds a LINK, so it is the one that must never be
   * cut fine -- a clipped control does not ellipsise, it ceases to exist.
   *
   * `Table` sets a `title` only on a cell that renders to a string or number,
   * so every JSX cell here carries its own; and each Badge gets `max-w-full`
   * with its label in a `truncate` span, because an `inline-flex` badge is an
   * atomic inline box that a `truncate` ancestor hard-CLIPS.
   */
  const columns: Column<QuoteRow>[] = [
    {
      key: 'request_number',
      header: 'Quote',
      width: '16rem',
      // U-04: `request_number` IS in QuoteRequestViewSet.ordering_fields.
      sortable: true,
      render: (quote: QuoteRow) => (
        <div>
          <Link
            to={`/my-quotes/${quote.id}`}
            aria-label={`Open quote ${quote.request_number}`}
            title={quote.request_number}
            className="block truncate text-sm font-medium text-primary-600 hover:underline dark:text-primary-400"
          >
            {quote.request_number}
          </Link>
          <p className="truncate text-xs text-gray-500 dark:text-gray-400" title={quote.broker_name}>
            {quote.broker_name}
          </p>
        </div>
      ),
    },
    {
      key: 'insurance',
      header: 'Insurance',
      width: '14rem',
      render: (quote: QuoteRow) => (
        <div>
          <p
            className="truncate text-sm text-gray-900 dark:text-white"
            title={quote.insurance_type_name}
          >
            {quote.insurance_type_name}
          </p>
          <p
            className="truncate text-xs text-gray-500 dark:text-gray-400"
            title={titleCase(quote.business_type)}
          >
            {titleCase(quote.business_type)}
          </p>
        </div>
      ),
    },
    {
      key: 'status',
      header: 'Status',
      width: '13rem',
      className: 'px-3',
      render: (quote: QuoteRow) => (
        <div className="space-y-1">
          <div>
            <Badge variant={getStatusBadgeVariant(quote.status)} size="sm" className="max-w-full">
              <span className="truncate" title={titleCase(quote.status)}>
                {titleCase(quote.status)}
              </span>
            </Badge>
          </div>
          <div>
            <SharedStatusBadge isShared={quote.is_shared} />
          </div>
        </div>
      ),
    },
    {
      key: 'created_at',
      header: 'Requested',
      width: '10rem',
      className: 'px-3',
      // U-04: `created_at` IS in QuoteRequestViewSet.ordering_fields.
      sortable: true,
      // A plain string, so `Table` supplies the tooltip itself.
      render: (quote: QuoteRow) => formatRelativeTime(quote.created_at),
    },
  ];

  return (
    <div>
      <PageHeader
        title="My Quotes"
        subtitle={
          /* No count while loading or after a failure -- "0 quotes" under a
             heading is a claim about the customer's record, and neither state
             has earned it. The card below says what actually happened. */
          isError || isLoading
            ? undefined
            : `${totalItems} quote${totalItems === 1 ? '' : 's'}`
        }
        breadcrumbs={[{ label: 'My Quotes' }]}
        actions={
          /* The ONE write affordance on this page, and it only navigates --
             the create itself happens on the journey's last step. */
          <Link
            to="/my-quotes/new"
            className="inline-flex items-center gap-1.5 rounded-md bg-primary-600 px-4 py-2 text-sm font-medium text-white hover:bg-primary-700"
          >
            <PlusIcon className="h-4 w-4" />
            Request a Quote
          </Link>
        }
      />

      {/* C7: search + filters -- READ-ONLY, same as the rest of this page (see
          the header comment). The only control added since is the header's
          "Request a Quote" link, which navigates and writes nothing. */}
      <CompactFilterBar
        search={searchTerm}
        onSearchChange={setSearchTerm}
        searchPlaceholder="Quote number, proposer name, phone..."
        activeFilterCount={activeFilterCount}
        onClearFilters={clearFilters}
        columns={4}
      >
        <SearchableSelect
          id="my-quotes-filter-insurance-type"
          label="Insurance Type"
          options={insuranceTypeOptions}
          value={filters.insurance_type}
          onChange={handleInsuranceTypeChange}
          placeholder="All Insurance Types"
        />
        <SearchableSelect
          id="my-quotes-filter-insurer"
          label="Insurance Company"
          options={insurerOptions}
          value={filters.insurer}
          onChange={handleInsurerChange}
          placeholder="All Insurance Companies"
        />
        <Select
          id="my-quotes-filter-status"
          label="Status"
          options={STATUS_FILTER_OPTIONS}
          value={filters.status}
          onChange={(e) => handleStatusChange(e.target.value)}
        />
        <DateRangePicker
          id="my-quotes-filter-date-range"
          label="Requested Date"
          fromValue={filters.created.from || ''}
          toValue={filters.created.to || ''}
          onChange={handleDateRangeChange}
          placeholder="All dates"
        />
      </CompactFilterBar>

      {isLoading ? (
        // The shared Table's own skeleton, wrapped so the "did not render the
        // empty state while loading" test still has something to point at.
        <div data-testid="my-quotes-skeleton">
          <Table<QuoteRow> data={[]} columns={columns} isLoading rowKey={(quote) => quote.id} />
        </div>
      ) : isError ? (
        /* NOT the empty state. We do not know that there are no quotes — we
           know we failed to ask. Saying "no quotes yet" here would be the
           platform telling a customer something false about their own
           record. */
        <Card padding="none">
          <EmptyState
            title="We could not load your quotes"
            description="Something went wrong reaching your broker. Your quotes have not been lost — please try again."
            action={
              <button
                type="button"
                onClick={() => refetch()}
                className="rounded-md bg-primary-600 px-4 py-2 text-sm font-medium text-white hover:bg-primary-700"
              >
                Try again
              </button>
            }
          />
        </Card>
      ) : quotes.length === 0 ? (
        // C7: a filtered-to-nothing result is a different fact from an
        // empty book -- "no quotes yet" would be false for a customer who
        // has quotes but none matching this search/filter combination.
        <Card padding="none">
          {hasActiveFilters ? (
            <EmptyState
              icon={<ClipboardDocumentListIcon />}
              title="No quotes match these filters"
              description="Try a different search term or clear the filters above."
              action={
                <button
                  type="button"
                  onClick={clearFilters}
                  className="rounded-md bg-primary-600 px-4 py-2 text-sm font-medium text-white hover:bg-primary-700"
                >
                  Clear filters
                </button>
              }
            />
          ) : (
            <EmptyState
              icon={<ClipboardDocumentListIcon />}
              title="No quotes yet"
              description="When your broker prepares a quote for you it will appear here — or ask for one yourself."
              action={
                <Link
                  to="/my-quotes/new"
                  className="rounded-md bg-primary-600 px-4 py-2 text-sm font-medium text-white hover:bg-primary-700"
                >
                  Request a Quote
                </Link>
              }
            />
          )}
        </Card>
      ) : (
        <>
          {/* No `onRowClick`: this page is read-only and the row is not a
              control. The one link on it goes to this portal's own read-only
              detail, never to the broker-side `/quotes/:id`. */}
          <Table<QuoteRow>
            data={quotes}
            columns={columns}
            rowKey={(quote) => quote.id}
            layout="fixed"
            stickyHeader
            sortKey={sortKey || undefined}
            sortDirection={sortDirection}
            onSortChange={handleSortChange}
          />
          {totalPages > 1 && (
            <Card padding="none" className="mt-4 overflow-hidden">
              <Pagination
                currentPage={page}
                totalPages={totalPages}
                totalItems={totalItems}
                itemsPerPage={ITEMS_PER_PAGE}
                onPageChange={setPage}
              />
            </Card>
          )}
        </>
      )}
    </div>
  );
}
